<template>
  <div 
    class="knowledge-list" 
    style="height:100%;overflow:auto;position:relative;background:var(--bg2)"
    @drop="handleFileDrop"
    @dragover.prevent
    @dragenter.prevent
  >
    <BaseEmpty v-if="hasNoContent" style="filter:grayscale(100%);opacity:0.3;" type="empty" msg="暂无内容" />
    
    <!-- 图标视图 -->
    <div v-else-if="viewMode === 'icon'" class="icon-view" @click="handleContainerClick">
      <!-- 子目录 -->
      <div
        v-for="sub in subdirs"
        :key="'dir-'+sub.id"
        class="icon-item icon-item-dir"
        :class="{ 'drop-target': dirEditable }"
        v-droppable="dirEditable ? handleDropInSubdir : null"
        :data-dir-id="sub.id"
        @click.stop="$emit('on-dir-select', sub)"
        @contextmenu.prevent="showItemContextMenu($event, null, sub)"
      >
        <div class="icon-item-content">
          <div class="icon-item-icon-wrapper">
            <BaseIcon icon="md-folder" :size="48" color="#faad14" />
          </div>
          <div class="icon-item-name">{{ sub.name }}</div>
        </div>
      </div>
      <div
        v-for="item in items"
        :key="item.id"
        class="icon-item"
        :class="{ 'selected': isSelected(item.id) }"
        v-draggable="dirEditable ? item.id : null"
        v-droppable="handleDropInFolder"
        :data-item-id="item.id"
        @click.stop="handleItemClick(item)"
        @dblclick="handleItemDoubleClick(item)"
        @contextmenu.prevent="showItemContextMenu($event, item, null)"
      >
        <div class="icon-item-content">
          <div class="icon-item-icon-wrapper">
            <BaseIcon :icon="getItemIcon(item)" :size="48" :color="getIconColor(item)" />
            <BaseIcon v-if="item.ref" icon="lianjie" :size="16" class="ref-badge-icon" />
          </div>
          <div class="icon-item-name">{{ item.name || getItemName(item) }}</div>
          <div class="icon-item-status">
            <span v-if="isAuditPending(item)" class="icon-status-tag pending">未审核</span>
            <span v-if="getRagLabel(item)" class="icon-status-tag" :class="getRagClass(item)">{{ getRagLabel(item) }}</span>
            <span v-if="showMarkdownBtn(item)" class="icon-status-tag md" :class="{ done: item.has_markdown || item.markdown_content }">MD</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 列表视图 -->
    <div v-else-if="viewMode === 'list'" class="list-view">
      <div
        v-for="sub in subdirs"
        :key="'dir-'+sub.id"
        class="list-item list-item-dir"
        :class="{ 'drop-target': dirEditable }"
        v-droppable="dirEditable ? handleDropInSubdir : null"
        :data-dir-id="sub.id"
        @click.stop="$emit('on-dir-select', sub)"
        @contextmenu.prevent="showItemContextMenu($event, null, sub)"
      >
        <div class="list-item-icon">
          <BaseIcon icon="md-folder" :size="24" color="#faad14" />
        </div>
        <div class="list-item-info">
          <div class="list-item-name"><span class="item-title">{{ sub.name }}</span></div>
          <div class="list-item-meta"><span class="meta-item">子目录</span></div>
        </div>
      </div>
      <div
        v-for="item in items"
        :key="item.id"
        class="list-item"
        :class="{ 'selected': isSelected(item.id) }"
        v-draggable="dirEditable ? item.id : null"
        v-droppable="handleDropInFolder"
        :data-item-id="item.id"
        @click.stop="handleItemClick(item)"
        @contextmenu.prevent="showItemContextMenu($event, item, null)"
      >
        <Checkbox
          :value="isSelected(item.id)"
          @click.stop.native="handleCheckboxClick(item)"
        />
        <div class="list-item-icon">
          <BaseIcon :icon="getItemIcon(item)" :size="24" :color="getIconColor(item)" />
          <BaseIcon v-if="item.ref" icon="lianjie" :size="14" class="ref-badge-icon" />
        </div>
        <div class="list-item-info">
          <div class="list-item-name">
            <span class="item-title">{{ item.name || getItemName(item) }}</span>
          </div>
          <div class="list-item-meta">
            <span class="meta-item">
              <span class="meta-label">上传:</span>
              <BaseNameBoard v-if="item.created_by" :uid="item.created_by" :size="16" hide-info />
              <span v-else>-</span>
              <span style="margin-left:5px;">{{ formatDate(item.created || item.upload_time) }}</span>
            </span>
            <span class="meta-item" v-if="item.updated_by || item.modifier">
              <span class="meta-label">修改:</span>
              <BaseNameBoard v-if="item.updated_by || item.modifier" :uid="item.updated_by || item.modifier" :size="16" hide-info />
              <span v-else>-</span>
              <span style="margin-left:5px;">{{ formatDate(item.updated) }}</span>
            </span>
            <span v-if="item.good > 0" class="meta-badge good">
              <BaseIcon icon="md-thumbs-up" :size="12" style="margin-right:2px;" />
              {{ item.good }}
            </span>
            <span v-if="item.bad > 0" class="meta-badge bad">
              <BaseIcon icon="md-thumbs-down" :size="12" style="margin-right:2px;" />
              {{ item.bad }}
            </span>
            <span v-if="item.comment_count > 0" class="meta-badge">
              <BaseIcon icon="md-chatbubbles" :size="12" style="margin-right:2px;" />
              {{ item.comment_count }}
            </span>
            <span v-if="isAuditPending(item)" class="meta-badge status-pending">未审核</span>
            <span v-if="getRagLabel(item)" class="meta-badge" :class="getRagClass(item)">{{ getRagLabel(item) }}</span>
          </div>
        </div>
        <div class="list-item-actions">
          <Button
            v-if="showMarkdownBtn(item)"
            type="text"
            size="small"
            :class="item.has_markdown || item.markdown_content ? 'md-btn-done' : 'md-btn-missing'"
            @click.stop="$emit('on-markdown', item)"
          >
            MD
          </Button>
          <Button
            v-if="selectedItems.length === 1 && isSelected(item.id)"
            type="text"
            icon="md-create"
            size="small"
            @click.stop="handleEdit(item)"
          >编辑</Button>
        </div>
      </div>
    </div>

    <!-- 摘要视图 -->
    <div v-else-if="viewMode === 'brief'" class="brief-view">
      <div
        v-for="sub in subdirs"
        :key="'dir-'+sub.id"
        class="brief-item brief-item-dir"
        :class="{ 'drop-target': dirEditable }"
        v-droppable="dirEditable ? handleDropInSubdir : null"
        :data-dir-id="sub.id"
        @click.stop="$emit('on-dir-select', sub)"
        @contextmenu.prevent="showItemContextMenu($event, null, sub)"
      >
        <div class="brief-item-header">
          <div class="brief-item-icon">
            <BaseIcon icon="md-folder" :size="24" color="#faad14" />
          </div>
          <div class="brief-item-title">{{ sub.name }}</div>
        </div>
        <div class="brief-item-content">子目录</div>
      </div>
      <div
        v-for="item in items"
        :key="item.id"
        class="brief-item"
        :class="{ 'selected': isSelected(item.id) }"
        v-draggable="dirEditable ? item.id : null"
        v-droppable="handleDropInFolder"
        :data-item-id="item.id"
        @click.stop="handleItemClick(item)"
        @contextmenu.prevent="showItemContextMenu($event, item, null)"
      >
        <div class="brief-item-header">
          <div class="brief-item-icon">
            <BaseIcon :icon="getItemIcon(item)" :size="24" :color="getIconColor(item)" />
            <BaseIcon v-if="item.ref" icon="lianjie" :size="14" class="ref-badge-icon" />
          </div>
          <div class="brief-item-title">
            <div v-if="item.name" class="item-title-row">
              <span class="item-title">{{ item.name }}</span>
             
            </div>
            <span v-else>{{ getItemName(item) }}</span>
          </div>
          <div class="brief-item-meta">
            <span class="meta-item">
              <span class="meta-label">上传:</span>
              <BaseNameBoard v-if="item.created_by || item.uploader" :uid="item.created_by || item.uploader" :size="16" hide-info />
              <span v-else>-</span>
              <span style="margin-left:5px;">{{ formatDate(item.created || item.upload_time) }}</span>
            </span>
            <span class="meta-item" v-if="item.updated_by || item.modifier">
              <span class="meta-label">修改:</span>
              <BaseNameBoard v-if="item.updated_by || item.modifier" :uid="item.updated_by || item.modifier" :size="16" hide-info />
              <span v-else>-</span>
              <span style="margin-left:5px;">{{ formatDate(item.updated) }}</span>
            </span>
            <span v-if="item.good > 0" class="meta-badge good">
              <BaseIcon icon="md-thumbs-up" :size="12" style="margin-right:2px;" />
              {{ item.good }}
            </span>
            <span v-if="item.bad > 0" class="meta-badge bad">
              <BaseIcon icon="md-thumbs-down" :size="12" style="margin-right:2px;" />
              {{ item.bad }}
            </span>
            <span v-if="item.comment_count > 0" class="meta-badge">
              <BaseIcon icon="md-chatbubbles" :size="12" style="margin-right:2px;" />
              {{ item.comment_count }}
            </span>
          </div>
        </div>
        <div class="brief-item-content" v-html="getItemPreview(item)"></div>
        <div class="brief-item-footer">
          <Button type="primary" size="small" @click.stop="handleOpen(item)">打开</Button>
        </div>
      </div>
    </div>

    <!-- 选择框 -->
    <div
      v-if="selecting"
      class="selection-box"
      :style="selectionBoxStyle"
      @mousedown.stop
    ></div>

  </div>
</template>

<script>
import BaseEmpty from '@/components/base/Empty';
import BaseIcon from '@/components/base/Icon';
import BaseNameBoard from '@/components/base/NameBoard';
import moment from 'moment';

export default {
  components: {BaseEmpty, BaseIcon, BaseNameBoard},
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    subdirs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    viewMode: {
      type: String,
      default: 'icon',
    },
    allItems: {
      type: Array,
      default: () => [],
    },
    dirEditable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedItems: [],
      selecting: false,
      selectionStart: null,
      selectionBoxStyle: {},
    };
  },
  computed: {
    hasNoContent() {
      const hasItems = this.items && this.items.length > 0;
      const hasSubdirs = this.subdirs && this.subdirs.length > 0;
      return !hasItems && !hasSubdirs;
    },
  },
  mounted() {
    this.$el.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    this.$el.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  methods: {
    getItemIcon(item) {
      // 如果是文件类型，根据文件扩展名返回不同图标
      if (item.type === 'file' && item.content) {
        const url = item.content;
        const fileName = url.split('/').pop() || '';
        const ext = fileName.split('.').pop().toLowerCase();
        return this.getFileIconByExt(ext);
      }
      
      // 其他类型使用固定图标
      const iconMap = {
        text: 'md-document',      // 文本图标（与用户修改保持一致）
        md: 'codelibrary-fill',   // Markdown 代码库图标
        html: 'css',              // HTML/CSS 图标
        ref: 'lianjie',           // 链接图标
      };
      return iconMap[item.type] || 'wenjian';
    },
    getFileIconByExt(ext) {
      const extIconMap = {
        // 文档类型
        'txt': 'md-document-text',
        'doc': 'md-document',
        'docx': 'md-document',
        'pdf': 'md-document',
        'xls': 'md-document',
        'xlsx': 'md-document',
        'ppt': 'md-document',
        'pptx': 'md-document',
        // 代码类型
        'js': 'codelibrary-fill',
        'ts': 'codelibrary-fill',
        'jsx': 'codelibrary-fill',
        'tsx': 'codelibrary-fill',
        'vue': 'codelibrary-fill',
        'py': 'codelibrary-fill',
        'java': 'codelibrary-fill',
        'cpp': 'codelibrary-fill',
        'c': 'codelibrary-fill',
        'go': 'codelibrary-fill',
        'php': 'codelibrary-fill',
        'rb': 'codelibrary-fill',
        // 标记语言
        'md': 'codelibrary-fill',
        'html': 'css',
        'htm': 'css',
        'xml': 'css',
        'json': 'codelibrary-fill',
        'yaml': 'codelibrary-fill',
        'yml': 'codelibrary-fill',
        // 图片类型
        'jpg': 'images',
        'jpeg': 'images',
        'png': 'images',
        'gif': 'images',
        'svg': 'images',
        'webp': 'images',
        'bmp': 'images',
        // 压缩文件
        'zip': 'wenjian',
        'rar': 'wenjian',
        '7z': 'wenjian',
        'tar': 'wenjian',
        'gz': 'wenjian',
        // 视频音频
        'mp4': 'shipin',
        'avi': 'shipin',
        'mov': 'shipin',
        'mp3': 'shipin',
        'wav': 'shipin',
      };
      return extIconMap[ext] || 'wenjian';
    },
    getIconColor(item) {
      // 根据类型返回不同的颜色
      const colorMap = {
        file: 'var(--primary)',
        text: '#1890ff',
        md: '#52c41a',
        html: '#fa8c16',
        ref: '#722ed1',
      };
      
      // 如果是文件类型，根据扩展名返回颜色
      if (item.type === 'file' && item.content) {
        const url = item.content;
        const fileName = url.split('/').pop() || '';
        const ext = fileName.split('.').pop().toLowerCase();
        
        // 代码文件
        if (['js', 'ts', 'jsx', 'tsx', 'vue', 'py', 'java', 'cpp', 'c', 'go', 'php', 'rb'].includes(ext)) {
          return '#52c41a';
        }
        // 标记语言
        if (['md', 'html', 'htm', 'xml', 'json', 'yaml', 'yml'].includes(ext)) {
          return ext === 'html' || ext === 'htm' ? '#fa8c16' : '#52c41a';
        }
        // 图片
        if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
          return '#eb2f96';
        }
        // 视频音频
        if (['mp4', 'avi', 'mov', 'mp3', 'wav'].includes(ext)) {
          return '#13c2c2';
        }
      }
      
      return colorMap[item.type] || 'var(--primary)';
    },
    getItemName(item) {
      // 如果是引用类型，使用源文件的名称
      if (item.ref && item.content) {
        const sourceItem = this.allItems.find(i => i.id === item.content);
        if (sourceItem) {
          return this.getItemName(sourceItem);
        }
      }
      
      if (item.type === 'file' && item.content) {
        const url = item.content;
        const fileName = url.split('/').pop() || '未命名文件';
        return fileName;
      }
      if (item.type === 'text' || item.type === 'md' || item.type === 'html') {
        const content = item.content || '';
        const firstLine = content.split('\n')[0] || '未命名文档';
        return firstLine.substring(0, 30) + (firstLine.length > 30 ? '...' : '');
      }
      return '未命名';
    },
    getItemPreview(item) {
      if (!item.content) return '暂无内容';
      if (item.type === 'file') {
        return `<a href="${item.content}" target="_blank">${this.getItemName(item)}</a>`;
      }
      if (item.type === 'text' || item.type === 'md') {
        const content = item.content.substring(0, 200);
        return content + (item.content.length > 200 ? '...' : '');
      }
      if (item.type === 'html') {
        const div = document.createElement('div');
        div.innerHTML = item.content;
        return div.textContent || div.innerText || 'HTML内容';
      }
      return '暂无预览';
    },
    isAuditPending(item) {
      if (!item || item.type === 'ref') return false;
      const s = item.audit_status;
      if (s === 'approved') return false;
      return s === 'pending' || s === 'unreviewed' || s == null || s === 0;
    },
    getRagLabel(item) {
      if (!item || item.rag_status == null || item.rag_status === '') return '';
      const done = item.rag_status === 'done' || item.rag_status === 'completed' || item.rag_status === true;
      return done ? 'RAG已上传' : 'RAG待处理';
    },
    getRagClass(item) {
      const done = item.rag_status === 'done' || item.rag_status === 'completed' || item.rag_status === true;
      return done ? 'rag-done' : 'rag-pending';
    },
    showMarkdownBtn(item) {
      if (!item || item.ref || item.type === 'md') return false;
      return item.type === 'file';
    },
    formatDate(date) {
      if (!date) return '';
      return moment(date).format('YYYY-MM-DD HH:mm');
    },
    isSelected(id) {
      return this.selectedItems.some(item => item.id === id);
    },
    handleItemClick(item) {
      if (this.viewMode === 'icon') {
        // 图标模式下，点击切换选择
        const index = this.selectedItems.findIndex(i => i.id === item.id);
        if (index >= 0) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems = [item];
        }
        this.$emit('on-select', this.selectedItems);
      } else {
        // 其他模式下，点击选择
        if (!this.isSelected(item.id)) {
          this.selectedItems = [item];
          this.$emit('on-select', this.selectedItems);
        }
      }
    },
    handleItemDoubleClick(item) {
      if (this.viewMode === 'icon') {
        this.$emit('on-open', item);
      }
    },
    handleCheckboxClick(item) {
      const index = this.selectedItems.findIndex(i => i.id === item.id);
      if (index >= 0) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(item);
      }
      this.$emit('on-select', this.selectedItems);
    },
    handleEdit(item) {
      this.$emit('on-edit', item);
    },
    handleOpen(item) {
      this.$emit('on-open', item);
    },
    handleContainerClick(e) {
      if (e.target === this.$el || e.target.classList.contains('icon-view')) {
        this.selectedItems = [];
        this.$emit('on-select', []);
      }
    },
    handleDropInFolder(draggedId, targetData) {
      if (!targetData || !targetData.itemId) return;
      this.$emit('on-drop-in-folder', {
        draggedId,
        targetItemId: targetData.itemId,
      });
    },
    handleDropInSubdir(draggedId, targetData) {
      if (!targetData || !targetData.dirId) return;
      this.$emit('on-drop-in-dir', {
        draggedId,
        targetDirId: targetData.dirId,
      });
    },
    handleMouseDown(e) {
      if (this.viewMode !== 'icon') return;
      if (e.target.closest('.icon-item')) return;
      
      this.selecting = true;
      const rect = this.$el.getBoundingClientRect();
      this.selectionStart = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      this.selectionBoxStyle = {
        left: this.selectionStart.x + 'px',
        top: this.selectionStart.y + 'px',
        width: '0px',
        height: '0px',
        display: 'block',
      };
    },
    handleMouseMove(e) {
      if (!this.selecting) return;
      const rect = this.$el.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      const left = Math.min(this.selectionStart.x, currentX);
      const top = Math.min(this.selectionStart.y, currentY);
      const width = Math.abs(currentX - this.selectionStart.x);
      const height = Math.abs(currentY - this.selectionStart.y);
      
      this.selectionBoxStyle = {
        left: left + 'px',
        top: top + 'px',
        width: width + 'px',
        height: height + 'px',
        display: 'block',
      };

      // 选择框内的项目
      const selected = [];
      this.items.forEach(item => {
        const itemEl = this.$el.querySelector(`[data-item-id="${item.id}"]`);
        if (itemEl) {
          const itemRect = itemEl.getBoundingClientRect();
          const itemLeft = itemRect.left - rect.left;
          const itemTop = itemRect.top - rect.top;
          const itemRight = itemLeft + itemRect.width;
          const itemBottom = itemTop + itemRect.height;
          
          if (itemLeft < left + width && itemRight > left &&
              itemTop < top + height && itemBottom > top) {
            selected.push(item);
          }
        }
      });
      this.selectedItems = selected;
      this.$emit('on-select', selected);
    },
    handleMouseUp() {
      if (this.selecting) {
        this.selecting = false;
        this.selectionBoxStyle = { display: 'none' };
      }
    },
    handleFileDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) {
        this.$emit('on-file-drop', files);
      }
    },
    showItemContextMenu(e, item, subdir) {
      const menuItem = item || null;
      const menuSubdir = subdir || null;
      this.$contextmenu({
        event: e,
        items: [
          { label: '打开', onClick: () => {
            if (menuSubdir) this.$emit('on-dir-select', menuSubdir);
            else if (menuItem) this.$emit('on-open', menuItem);
          }},
          { label: '编辑', disabled: !menuItem, onClick: () => {
            if (menuItem) this.$emit('on-context-menu', { action: 'edit', item: menuItem });
          }},
          { label: '删除', disabled: !menuItem, onClick: () => {
            if (menuItem) this.$emit('on-context-menu', { action: 'remove', item: menuItem });
          }},
        ],
        zIndex: 9999,
        minWidth: 100,
      });
    },
  },
};
</script>

<style lang="less" scoped>

.icon-view {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 20px;

  .icon-item {
    width: 128px;
    min-height: 128px;
    border: 1px solid var(--border);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    padding: 12px;
    background: var(--bg3);

    &:hover {
      background: rgba(51, 153, 255, 0.08);
      border-color: rgba(51, 153, 255, 0.4);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    &.selected {
      background: rgba(51, 153, 255, 0.15);
      border-color: var(--primary);
      box-shadow: 0 2px 8px rgba(51, 153, 255, 0.2);
    }

    &.icon-item-dir:hover {
      border-color: rgba(250, 173, 20, 0.5);
      background: rgba(250, 173, 20, 0.06);
    }

    .icon-item-content {
      text-align: center;
      padding: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      
      .icon-item-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        border-radius: 4px;
        background: transparent;
        margin: 0 auto 8px;
        position: relative;
        flex-shrink: 0;
        
        .ref-badge-icon {
          position: absolute;
          bottom: 0;
          right: 0;
          background: var(--primary);
          color: #fff;
          border-radius: 50%;
          padding: 2px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      }

      .icon-item-status {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        justify-content: center;
        margin-top: 6px;

        .icon-status-tag {
          font-size: 10px;
          padding: 1px 5px;
          border-radius: 3px;
          background: var(--bg2);
          border: 1px solid var(--border);
          color: var(--subtext2);

          &.pending {
            color: #d48806;
            border-color: rgba(250, 173, 20, 0.4);
          }

          &.rag-done {
            color: #52c41a;
          }

          &.rag-pending {
            color: #722ed1;
          }

          &.md.done {
            color: #52c41a;
            font-weight: 600;
          }
        }
      }

      .icon-item-name {
        margin-top: 8px;
        font-size: 12px;
        line-height: 1.4;
        word-wrap: break-word;
        word-break: break-all;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        max-height: 2.8em;
        text-align: center;
        padding: 0 4px;
      }
    }
  }
}

.list-view {
  padding: 0 12px 12px;

  .list-item {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    margin-bottom: 2px;
    border-radius: 6px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: var(--bg3);
      border-color: var(--border);
    }

    &.selected {
      background: var(--primary-light);
      border-color: rgba(51, 153, 255, 0.3);
    }


    .list-item-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 6px;
      background: var(--bg3);
      margin: 0 10px;
      flex-shrink: 0;
      position: relative;
      
      .ref-badge-icon {
        position: absolute;
        bottom: 0;
        right: 0;
        background: var(--primary);
        color: #fff;
        border-radius: 50%;
        padding: 1px;
        font-size: 10px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }
    }

    .list-item-info {
      flex: 1;
      margin-left: 10px;

      .list-item-name {
        font-weight: 500;
        margin-bottom: 5px;
      }

      .list-item-meta {
        font-size: 12px;
        color: var(--subtext2);
        display: flex;
        gap: 10px;
        align-items: center;
        
        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          
          .meta-label {
            color: var(--subtext3);
            margin-right: 2px;
          }
        }

        .meta-badge {
          padding: 3px 8px;
          background: var(--bg3);
          border-radius: 3px;
          font-size: 11px;
          display: flex;
          align-items: center;
          border: 1px solid var(--border);

          &.good {
            background: rgba(76, 175, 80, 0.1);
            color: #4caf50;
            border-color: rgba(76, 175, 80, 0.3);
          }

          &.bad {
            background: rgba(244, 67, 54, 0.1);
            color: #f44336;
            border-color: rgba(244, 67, 54, 0.3);
          }

          &.status-pending {
            background: rgba(250, 173, 20, 0.12);
            color: #d48806;
            border-color: rgba(250, 173, 20, 0.35);
          }

          &.rag-done {
            background: rgba(82, 196, 26, 0.1);
            color: #52c41a;
            border-color: rgba(82, 196, 26, 0.3);
          }

          &.rag-pending {
            background: rgba(114, 46, 209, 0.1);
            color: #722ed1;
            border-color: rgba(114, 46, 209, 0.3);
          }
        }
      }
    }

    .list-item-actions {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 4px;

      .md-btn-done {
        color: #52c41a;
        font-weight: 600;
      }

      .md-btn-missing {
        color: var(--subtext3);
      }
    }
  }
}

.brief-view {
  padding: 16px 20px;

  .brief-item {
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 16px 18px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s;
    background: var(--bg3);

    &:hover {
      border-color: var(--primary);
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    }

    &.selected {
      border-color: var(--primary);
      background: var(--primary-light);
      box-shadow: 0 2px 12px rgba(51, 153, 255, 0.15);
    }


    .brief-item-header {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      
      .brief-item-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 6px;
        background: var(--bg3);
        margin-right: 10px;
        flex-shrink: 0;
        position: relative;
        
        .ref-badge-icon {
          position: absolute;
          bottom: 0;
          right: 0;
          background: var(--primary);
          color: #fff;
          border-radius: 50%;
          padding: 1px;
          font-size: 10px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }
      }

      .brief-item-title {
        flex: 1;
        font-weight: 500;
        font-size: 16px;
      }

      .brief-item-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 12px;
        color: var(--subtext2);
        
        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          
          .meta-label {
            color: var(--subtext3);
            margin-right: 2px;
          }
        }

        .meta-badge {
          padding: 2px 6px;
          background: var(--border);
          border-radius: 3px;
          font-size: 11px;

          &.good {
            background: rgba(76, 175, 80, 0.2);
            color: #4caf50;
          }

          &.bad {
            background: rgba(244, 67, 54, 0.2);
            color: #f44336;
          }
        }
      }
    }

    .brief-item-content {
      color: var(--subtext2);
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 10px;
      max-height: 100px;
      overflow: hidden;
    }

    .brief-item-footer {
      display: flex;
      justify-content: flex-end;
    }
  }
}

.selection-box {
  position: absolute;
  border: 2px dashed var(--primary);
  background: rgba(51, 153, 255, 0.1);
  pointer-events: none;
  z-index: 10;
  display: none;
}

.ugo-draggable {
  opacity: 0.5;
}

.ugo-droppable {
  background: var(--primary-light) !important;
  border: 2px dashed var(--primary) !important;
}

.icon-item-dir.drop-target,
.list-item-dir.drop-target,
.brief-item-dir.drop-target {
  transition: box-shadow 0.2s, transform 0.2s;
}

.icon-item-dir.ugo-droppable,
.list-item-dir.ugo-droppable,
.brief-item-dir.ugo-droppable {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(51, 153, 255, 0.25);
}

</style>
